import React, { useMemo } from 'react';
import { Minus, Plus } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { useCategoryRevamp } from '@modules/category/hook/use-category-revamp';
import {
    parseFooterContentInBrowser,
    type ParsedFooterFAQContent,
} from '~/modules/professionals-category/util/parse-footer-content-in-browser';
import { replaceEmDashes } from '~/modules/professionals-category/util/replace-em-dashes';
import { useDirectoryBrowseTheme } from '~/ui-rocco/pages/directory-browse/directory-browse-theme';

/**
 * Interface representing a single FAQ element with title, question and answer
 * @interface FAQElement
 * @property {string} content - The full content of the FAQ
 * @property {string} title - The title/heading of the FAQ
 * @property {string} question - The FAQ question text
 * @property {string} answer - The FAQ answer text
 */
interface FAQElement {
    content: string;
    title: string;
    question?: string;
    answer?: string;
}

/**
 * Props interface for the CategoryPageFAQ component
 * @interface CategoryPageFAQProps
 * @property {FAQElement[]} faqSchemas - Array of FAQ items to display
 * @property {string} footerContent - HTML content to display in the header/footer section
 * @property {'accordion' | 'grid'} [variant='accordion'] - Display variant
 * @property {string} [categoryLabel] - Category label for grid variant header
 * @property {string} [seoHeading] - Visible h2 for the category SEO copy block
 * @property {ParsedFooterFAQContent} [parsedFooterContent] - Server-parsed footer for grid SSR
 * @property {string} [fullTeaserHtml] - The whole category teaser: in the grid, under the footer's large intro;
 *   in the accordion, ahead of the footer prose. Under its own heading when no SEO heading opens the block
 * @property {string} [teaserHeading] - Visible h2 for that teaser block, e.g. "About Building products"
 */
interface CategoryPageFAQProps {
    faqSchemas: FAQElement[];
    footerContent: string;
    variant?: 'accordion' | 'grid';
    categoryLabel?: string;
    seoHeading?: string;
    parsedFooterContent?: ParsedFooterFAQContent;
    fullTeaserHtml?: string;
    teaserHeading?: string;
}

/**
 * The category teaser the intro leaves out, under its own heading: the whole copy the hero was cut
 * from, or the archived original once a shorter hero replaced it. Served visible, as one passage:
 * heading plus prose, never split into columns.
 */
const FullTeaserBlock = ({
    html,
    heading,
    textClass,
}: {
    html: string;
    heading?: string;
    textClass: string;
}) => (
    <div className="flex flex-col gap-4">
        {heading && (
            <h2
                className={cn(
                    'text-6 font-medium tracking-[0.025rem]',
                    textClass,
                )}
            >
                {heading}
            </h2>
        )}
        <div
            className={cn(
                'cms-inline-html directory-teaser-full text-4 leading-[1.5] md:max-w-370',
                textClass,
            )}
            dangerouslySetInnerHTML={{ __html: replaceEmDashes(html) }}
        />
    </div>
);

/**
 * Grid variant component for static FAQ display (used on products pages)
 * Parses footerContent HTML to extract intro text and FAQ items
 * Displays FAQs in a newspaper-style 3-column layout
 */
const FAQGridVariant = ({
    footerContent,
    categoryLabel,
    seoHeading,
    parsedFooterContent,
    fullTeaserHtml,
    teaserHeading,
    faqSchemas,
}: {
    footerContent: string;
    categoryLabel?: string;
    seoHeading?: string;
    parsedFooterContent?: ParsedFooterFAQContent;
    fullTeaserHtml?: string;
    teaserHeading?: string;
    faqSchemas: FAQElement[];
}) => {
    // Flip to light text on the dark projects browse theme.
    const isDark = useDirectoryBrowseTheme() === 'dark';
    const textClass = isDark ? 'text-white' : 'text-[#323232]';
    const borderClass = isDark ? 'border-white/15' : 'border-gray-200';

    const clientParsedContent = useMemo(
        () => parseFooterContentInBrowser(footerContent),
        [footerContent],
    );
    const parsedContent = parsedFooterContent ?? clientParsedContent;
    // The SEO heading ("Learn about Architects") or the category label ("About
    // Bathroom products") already opens this block, and the teaser sits right
    // under it: a second heading of its own there would stack two h2s with
    // nothing between them, so the teaser reads as the section's first passage
    // instead.
    const shownTeaserHeading =
        seoHeading || categoryLabel ? undefined : teaserHeading;

    const introHtml = replaceEmDashes(parsedContent.introHtml);
    const faqItems = parsedContent.faqItems.map((faq) => ({
        title: replaceEmDashes(faq.title),
        content: replaceEmDashes(faq.content),
    }));
    const cleanedFooterContent = replaceEmDashes(footerContent);
    const showRawFallback =
        !introHtml && faqItems.length === 0 && Boolean(footerContent);

    if (
        !introHtml &&
        faqItems.length === 0 &&
        !categoryLabel &&
        !showRawFallback &&
        !fullTeaserHtml &&
        faqSchemas.length === 0
    ) {
        return null;
    }

    if (showRawFallback) {
        return (
            <div
                className={cn(
                    'flex flex-col gap-14.5 px-6 md:px-12',
                    textClass,
                )}
            >
                {seoHeading && (
                    <h2
                        className={cn(
                            'text-6 font-medium tracking-[0.025rem]',
                            textClass,
                        )}
                    >
                        {seoHeading}
                    </h2>
                )}
                {categoryLabel && (
                    <div className={cn('border-t pt-4', borderClass)}>
                        <h2
                            className={cn(
                                'text-4 font-normal tracking-[-0.03em]',
                                textClass,
                            )}
                        >
                            {categoryLabel}
                        </h2>
                    </div>
                )}
                {fullTeaserHtml && (
                    <FullTeaserBlock
                        html={fullTeaserHtml}
                        heading={shownTeaserHeading}
                        textClass={textClass}
                    />
                )}
                <div
                    className="faq-footer"
                    dangerouslySetInnerHTML={{ __html: cleanedFooterContent }}
                />
                <FaqDetailsList faqSchemas={faqSchemas} startOpen={false} />
                <div className={cn('border-t pt-4', borderClass)} />
            </div>
        );
    }

    return (
        <div className={cn('flex flex-col gap-14.5 px-6 md:px-12', textClass)}>
            {seoHeading && (
                <h2
                    className={cn(
                        'text-6 font-medium tracking-[0.025rem]',
                        textClass,
                    )}
                >
                    {seoHeading}
                </h2>
            )}
            {/* Header Section - category label and hero intro */}
            {(categoryLabel || introHtml || fullTeaserHtml) && (
                <div className="flex flex-col gap-11">
                    {categoryLabel && (
                        <div className={cn('border-t pt-4', borderClass)}>
                            <h2
                                className={cn(
                                    'text-4 font-normal tracking-[-0.03em]',
                                    textClass,
                                )}
                            >
                                {categoryLabel}
                            </h2>
                        </div>
                    )}
                    {introHtml && (
                        <div
                            className={cn(
                                'cms-inline-html faq-grid-intro font-twk-ghost text-[2.15rem] leading-[1.4] font-normal tracking-tight md:max-w-370',
                                textClass,
                            )}
                            dangerouslySetInnerHTML={{ __html: introHtml }}
                        />
                    )}
                    {/* The teaser follows the large intro: the display-size
                        statement leads the block and the smaller prose reads
                        as what it opens onto. */}
                    {fullTeaserHtml && (
                        <FullTeaserBlock
                            html={fullTeaserHtml}
                            heading={shownTeaserHeading}
                            textClass={textClass}
                        />
                    )}
                </div>
            )}

            {/* FAQ Columns - newspaper-style layout flowing down then across */}
            {faqItems.length > 0 && (
                <div className="columns-1 gap-21.5 md:max-w-370 md:columns-3">
                    {faqItems.map((faq, index) => (
                        <div
                            key={`faq-grid-${index}`}
                            className="mb-10 flex break-inside-avoid flex-col gap-1.5 md:max-w-109"
                        >
                            <h3
                                className={cn(
                                    'text-3.5 leading-normal font-semibold tracking-[0.02em]',
                                    textClass,
                                )}
                            >
                                {faq.title}
                            </h3>
                            {faq.content && (
                                <div
                                    className={cn(
                                        'cms-inline-html faq-grid-answer text-3.5 leading-[1.4] font-normal tracking-[0.02em]',
                                        textClass,
                                    )}
                                    dangerouslySetInnerHTML={{
                                        __html: faq.content,
                                    }}
                                />
                            )}
                        </div>
                    ))}
                </div>
            )}
            {/* The CMS FAQ entries render here too: the JSON-LD for them is
                emitted at every width, so the questions it declares are on the
                page at every width. */}
            <FaqDetailsList faqSchemas={faqSchemas} startOpen={false} />
            <div className={cn('border-t pt-4', borderClass)} />
        </div>
    );
};

/**
 * The CMS FAQ entries as native details/summary rows, shared by both variants. Native rather than
 * the Rocco Accordion: Radix unmounts closed item content, which would strip the answers (and any
 * links in them) from the served DOM. Each answer stays mounted in both states — only display
 * toggles. Open state is tracked here (with onToggle) rather than a fixed `open` per row: a bare
 * `open` prop makes native `details` controlled, so React would re-assert it and revert the
 * user's toggle.
 */
const FaqDetailsList = ({
    faqSchemas,
    startOpen,
}: {
    faqSchemas: FAQElement[];
    startOpen: boolean;
}) => {
    const filteredFAQSchemas = React.useMemo(
        () => faqSchemas.filter((faq) => faq.question && faq.answer),
        [faqSchemas],
    );
    const [openFAQs, setOpenFAQs] = React.useState<Set<number>>(() =>
        startOpen ? new Set([0]) : new Set(),
    );
    const handleFAQToggle =
        (index: number) =>
        (event: React.SyntheticEvent<HTMLDetailsElement>) => {
            const isOpen = event.currentTarget.open;
            setOpenFAQs((prev) => {
                const next = new Set(prev);
                if (isOpen) next.add(index);
                else next.delete(index);
                return next;
            });
        };

    if (filteredFAQSchemas.length === 0) {
        return null;
    }

    return (
        <div>
            <h4 className="faq-footer-title">FAQs</h4>
            {filteredFAQSchemas.map((faq, index) => (
                <details
                    key={faq.title}
                    open={openFAQs.has(index)}
                    onToggle={handleFAQToggle(index)}
                    className="group/faq border-b border-b-gray-900/30"
                >
                    {/* flex! beats Aria's un-layered summary{display:list-item} normalize */}
                    <summary className="flex! min-h-11 cursor-pointer list-none items-center justify-between gap-3 py-3 font-medium marker:hidden [&::-webkit-details-marker]:hidden">
                        <span
                            className="text-left text-xl font-medium"
                            dangerouslySetInnerHTML={{ __html: faq.title }}
                        />
                        <Plus
                            aria-hidden="true"
                            className="h-4 w-4 shrink-0 group-open/faq:hidden"
                        />
                        <Minus
                            aria-hidden="true"
                            className="hidden h-4 w-4 shrink-0 group-open/faq:block"
                        />
                    </summary>
                    <div className="pb-4">
                        <div
                            className="cms-inline-html grid py-2"
                            dangerouslySetInnerHTML={{
                                __html: faq.answer || '',
                            }}
                        />
                    </div>
                </details>
            ))}
        </div>
    );
};

/**
 * Component that renders an FAQ section with expandable accordion items
 * or as a static grid layout depending on the variant prop
 * @param {CategoryPageFAQProps} props - Component props
 * @returns {React.JSX.Element | null} The rendered FAQ section or null if no content
 */
export const CategoryPageFAQ = (props: CategoryPageFAQProps) => {
    const {
        faqSchemas,
        footerContent,
        variant = 'accordion',
        categoryLabel,
        seoHeading,
        parsedFooterContent,
        fullTeaserHtml = '',
        teaserHeading,
    } = props;
    // Flip to light text on the dark projects browse theme.
    const isDark = useDirectoryBrowseTheme() === 'dark';
    const textClass = isDark ? 'text-white' : 'text-[#323232]';
    const borderClass = isDark ? 'border-white/15' : 'border-gray-200';
    const categoryRevamp = useCategoryRevamp();

    const hasFAQs = faqSchemas.some((faq) => faq.question && faq.answer);
    const showAccordionSeoHeading = Boolean(
        seoHeading && (footerContent || fullTeaserHtml),
    );

    // Grid variant - parses footerContent to extract FAQ items
    if (variant === 'grid') {
        if (!footerContent && !fullTeaserHtml && !hasFAQs) {
            return null;
        }
        return (
            <FAQGridVariant
                footerContent={footerContent}
                categoryLabel={categoryLabel}
                seoHeading={seoHeading}
                parsedFooterContent={parsedFooterContent}
                fullTeaserHtml={fullTeaserHtml}
                teaserHeading={teaserHeading}
                faqSchemas={faqSchemas}
            />
        );
    }

    // Early return if no content for accordion variant
    if (!hasFAQs && !footerContent && !fullTeaserHtml) {
        return null;
    }

    return (
        <div
            className={cn(
                'md:mb-16 md:px-12',
                // This block also closes the legacy category pages, which every
                // visitor sees while the revamp is off — so the phone gutter
                // and the CMS heading overrides follow the flag rather than
                // restyling those pages ahead of it.
                categoryRevamp
                    ? // The page's own 1rem gutter below md, level with the
                      // grid and the sections above rather than a step in.
                      'px-4'
                    : 'px-6',
                // The copy's own h2s below md take the directory's section
                // title (Recently viewed, Explore all categories), rather than
                // the CMS stylesheet's loose 1.75 leading and open tracking.
                categoryRevamp &&
                    'max-md:[&_.faq-footer_h2]:font-sans max-md:[&_.faq-footer_h2]:text-6 max-md:[&_.faq-footer_h2]:leading-[1.2] max-md:[&_.faq-footer_h2]:font-medium max-md:[&_.faq-footer_h2]:tracking-[-0.02em]',
                textClass,
            )}
        >
            <div className="flex flex-col pb-14.5 md:gap-12 md:pb-0">
                {/* The SEO heading opens the block, as on desktop, and the
                    teaser then reads as its first passage: a heading of the
                    teaser's own under it would stack two h2s. */}
                {showAccordionSeoHeading && (
                    <h2
                        className={cn(
                            'pt-6 text-6 font-medium tracking-[0.025rem] md:pt-0',
                            textClass,
                        )}
                    >
                        {seoHeading}
                    </h2>
                )}
                {fullTeaserHtml && (
                    <FullTeaserBlock
                        html={fullTeaserHtml}
                        heading={
                            showAccordionSeoHeading ? undefined : teaserHeading
                        }
                        textClass={textClass}
                    />
                )}
                {/* Footer content renders in full — it is the SEO prose, and
                    mobile-first indexing is where it earns its keep. No clamp,
                    no disclosure. */}
                {footerContent && (
                    <div
                        className="cms-inline-html faq-footer"
                        dangerouslySetInnerHTML={{
                            __html: replaceEmDashes(footerContent),
                        }}
                    />
                )}

                {/* Seeded open on the first question only when there's no
                    footer prose acting as the section's opening content — with
                    prose present every question starts collapsed. */}
                <FaqDetailsList
                    faqSchemas={faqSchemas}
                    startOpen={!footerContent && !fullTeaserHtml}
                />
            </div>
            <div className={cn('border-t pt-4', borderClass)} />
        </div>
    );
};
